import {Params} from '@angular/router';

/**
 * The step-up resume contract (Story 47.2, Dev Notes "The resume contract").
 *
 * OAuth mode — `/authorize` 302s to `/mfa/challenge|enroll` echoing every OAuth
 * param (exactly the set the 38.8 terms interstitial uses); the page resumes
 * with `window.location.href = '/authorize?' + the same set`.
 * Device mode — the device page (or the SDK's deviceApprove) navigates here
 * with `device_user_code` and `client_id`; the page resumes to
 * `/device?user_code=…`.
 * Story 53.9: no session in any of these URLs, and none in the context — the
 * session is the host-only, HttpOnly cookie the browser attaches to every /mfa/*
 * call and to the resume navigation. "Signed in?" is answered by /mfa/state
 * (401 → sign-in). Both modes carry `client_id` because every /mfa/*
 * call needs it (the backend checks the user is bound to that client) and
 * because 47.4 will serve operator pages by client over these exact URLs.
 */
export type StepUpMode = 'oauth' | 'device';

export interface StepUpContext {
  mode: StepUpMode;
  clientId: string;
  deviceUserCode: string;
  /** OAuth params in the order the terms interstitial emits them. */
  oauth: {
    response_type: string;
    redirect_uri: string;
    scope: string;
    state: string;
    audience: string;
    code_challenge: string;
    code_challenge_method: string;
    nonce: string;
  };
}

export function readStepUpContext(params: Params): StepUpContext {
  const q = (k: string): string => (params[k] ?? '').toString();
  const deviceUserCode = q('device_user_code').trim().toUpperCase();
  return {
    mode: deviceUserCode ? 'device' : 'oauth',
    clientId: q('client_id'),
    deviceUserCode,
    oauth: {
      response_type: q('response_type') || 'code',
      redirect_uri: q('redirect_uri'),
      scope: q('scope'),
      state: q('state'),
      audience: q('audience'),
      code_challenge: q('code_challenge'),
      code_challenge_method: q('code_challenge_method') || 'S256',
      nonce: q('nonce'),
    },
  };
}

/** Where the flow continues once the session is elevated. */
export function resumeUrl(ctx: StepUpContext): string {
  if (ctx.mode === 'device') {
    return `/device?user_code=${encodeURIComponent(ctx.deviceUserCode)}`;
  }
  const params = new URLSearchParams({
    response_type: ctx.oauth.response_type,
    client_id: ctx.clientId,
    redirect_uri: ctx.oauth.redirect_uri,
    scope: ctx.oauth.scope,
    state: ctx.oauth.state,
    audience: ctx.oauth.audience,
    code_challenge: ctx.oauth.code_challenge,
    code_challenge_method: ctx.oauth.code_challenge_method,
  });
  if (ctx.oauth.nonce) params.set('nonce', ctx.oauth.nonce);
  return `/authorize?${params.toString()}`;
}

/** The sibling step-up page (challenge ⇄ enroll) with the identical query string. */
export function siblingUrl(ctx: StepUpContext, path: '/mfa/challenge' | '/mfa/enroll'): string {
  const params = new URLSearchParams();
  if (ctx.mode === 'device') {
    params.set('device_user_code', ctx.deviceUserCode);
  } else {
    params.set('response_type', ctx.oauth.response_type);
    params.set('redirect_uri', ctx.oauth.redirect_uri);
    params.set('scope', ctx.oauth.scope);
    params.set('state', ctx.oauth.state);
    params.set('audience', ctx.oauth.audience);
    params.set('code_challenge', ctx.oauth.code_challenge);
    params.set('code_challenge_method', ctx.oauth.code_challenge_method);
    if (ctx.oauth.nonce) params.set('nonce', ctx.oauth.nonce);
  }
  params.set('client_id', ctx.clientId);
  return `${path}?${params.toString()}`;
}

/** Back to sign-in when the session is gone — return-aware in both modes. */
export function signInUrl(ctx: StepUpContext): string {
  if (ctx.mode === 'device') {
    return `/login?device_user_code=${encodeURIComponent(ctx.deviceUserCode)}`;
  }
  const params = new URLSearchParams({
    client_id: ctx.clientId,
    redirect_uri: ctx.oauth.redirect_uri,
    scope: ctx.oauth.scope,
    state: ctx.oauth.state,
    audience: ctx.oauth.audience,
    code_challenge: ctx.oauth.code_challenge,
    code_challenge_method: ctx.oauth.code_challenge_method,
  });
  if (ctx.oauth.nonce) params.set('nonce', ctx.oauth.nonce);
  return `/login?${params.toString()}`;
}
